import React, { useEffect, useState } from 'react';
import h337 from 'heatmap.js';
import { Loader } from '@playbookux/playbook-web-core';

import PropTypes from 'prop-types';
import {
  BaseImage,
  Centered,
  Container,
  ContainerHeatmap,
  OverlayImage,
} from './styles';

const POINT_RADIUS_SMALL_IMAGE = 20;
const POINT_RADIUS_LARGE_IMAGE = 100;

/**
 * EXTREMELY IMPORTANT TO KNOW.
 * HEATMAP.JS CREATES <CANVAS> TAG AND DOES NOT WORK WITH <IMAGE> TAG, IT ONLY WORKS WITH <DIV> TAG
 * WHEN YOU INTEGRATE THE HEATMAP.JS LIBRARY, DO NOT USE <IMAGE> TAG TO GET COORDINATES, USE <DIV> TAG INSTEAD
 * WE NO LONGER RESIZE THE ELEMENT DUE TO CUSTOMER REQUIREMENTS AT TESTER
 */
const Heatmap = (props) => {
  const {
    imageSrc, data, id,
  } = props;
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [fontColor] = useState('#ffffff');
  const [imageGetUrlHeatmap, setImageGetUrlHeatmap] = useState(null);

  useEffect(() => {
    const img = new Image();
    img.src = imageSrc;
    img.onload = (i) => {
      // We changed to using the native image size even due to customer requirements
      const { width, height } = i.target;
      setImageDimensions({ width, height });
      setImageLoaded(true);
    };
  }, [imageSrc]);

  useEffect(() => {
    const heatmapContainer = document.getElementById(`heatmapContainer-${id}`);

    if (imageLoaded && heatmapContainer) {
      const POINT_RADIUS = imageDimensions.width < 2000 || imageDimensions.height < 2000
        ? POINT_RADIUS_SMALL_IMAGE : POINT_RADIUS_LARGE_IMAGE;
      const heatmapInstance = h337.create({
        container: heatmapContainer,
        maxOpacity: 0.6,
        radius: POINT_RADIUS,
        blur: 0.9,
      });

      const points = data.map((result) => ({
        x: parseInt(result.firstClickCoordinates.x, 10),
        y: parseInt(result.firstClickCoordinates.y, 10),
        value: 1,
      }));

      heatmapInstance.setData({
        max: 1,
        data: points,
      });

      // Cluster points based on their radius
      const clusters = [];

      points.forEach((point) => {
        let addedToCluster = false;
        clusters.forEach((cluster, i) => {
          const dx = cluster.x - point.x;
          const dy = cluster.y - point.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < POINT_RADIUS) {
            cluster.points.push(point);
            clusters[i] = { x: point.x, y: point.y, points: cluster.points };
            addedToCluster = true;
          }
        });
        if (!addedToCluster) {
          clusters.push({ x: point.x, y: point.y, points: [point] });
        }
      });

      // Calculate total clicks
      const totalClicks = points.reduce((sum, point) => sum + point.value, 0);

      // Calculate clicks per cluster and percentages
      const clicksPerCluster = clusters.map((cluster) => cluster.points.reduce((sum, p) => sum + p.value, 0));

      const percentages = clicksPerCluster.map((clicks) => (clicks / totalClicks) * 100);

      // Overlay percentages on the heatmap
      // eslint-disable-next-line no-underscore-dangle
      const { canvas } = heatmapInstance._renderer;
      // eslint-disable-next-line no-underscore-dangle

      const ctx = canvas.getContext('2d');

      let fontSize;
      if (imageDimensions.width > 2000) {
        fontSize = (imageDimensions.width / 2000) * 24;
      } else {
        fontSize = 24;
      }
      ctx.font = `600 ${fontSize}px Arial`;
      ctx.fillStyle = fontColor;
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = fontSize * 0.05;
      clusters.forEach((cluster, i) => {
        const percentageFixed = parseFloat(percentages[i].toFixed(2));
        const text = `${percentageFixed}%`;
        ctx.strokeText(text, cluster.x, cluster.y);
        ctx.fillText(text, cluster.x, cluster.y);
      });

      setImageGetUrlHeatmap(heatmapInstance.getDataURL());
    }
  }, [data, fontColor, id, imageDimensions.height, imageDimensions.width, imageLoaded]);

  return (
    <>
      {!imageLoaded && (
        <Centered>
          <Loader data-loader="heatmap-loader" />
        </Centered>
      )}
      <ContainerHeatmap
        id={`heatmapContainer-${id}`}
        imageDimensionsWidth={imageDimensions.width}
        imageDimensionsHeight={imageDimensions.height}
        imageSrc={imageSrc}
      />
      <Container>
        {imageLoaded && <BaseImage src={imageSrc} alt="heatmap" isBrightness />}
        {imageLoaded && imageGetUrlHeatmap && <OverlayImage src={imageGetUrlHeatmap} alt="heatmap" />}
      </Container>
    </>
  );
};

Heatmap.propTypes = {
  imageSrc: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  })).isRequired,
  id: PropTypes.string.isRequired,
};

export default Heatmap;
